import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

type IconCSSClass = "wave";

export type ActivityPromptKind = "popup-blocked" | "popup-interrupted";

/**
 * something went wrong where the user was waiting, and only they can move it along. it takes the
 * spinner's place: a spinner that stays up while nothing is happening reads as progress.
 */
export interface IActivityPrompt {
  kind: ActivityPromptKind;
  /** i18n key */
  title: string;
  /** i18n key */
  body: string;
  /**
   * runs inside the click's call stack, so a window it opens counts as user-initiated and the
   * browser lets it through even while pop-ups are blocked.
   */
  retry: () => void;
}

@Injectable()
export class ActivityIndicatorService {
  private _visible = new Subject<boolean>();
  public isVisible$ = this._visible.asObservable();

  private _message = new Subject<string>();
  public message$ = this._message.asObservable();

  private _icon = new Subject<{ name: string; cssClass?: IconCSSClass }>();
  public icon$ = this._icon.asObservable();

  private _backdrop = new Subject<boolean>();
  public backdrop$ = this._backdrop.asObservable();

  private _prompt = new Subject<IActivityPrompt>();
  public prompt$ = this._prompt.asObservable();

  /** kept so that a retry can find the callback of the prompt on screen */
  private _currentPrompt: IActivityPrompt;

  public setMessage(message: string) {
    this._message.next(message);
  }

  private setBackdrop(visible: boolean) {
    this._backdrop.next(visible);
  }

  public error(message: string, timeoutSeconds?: number) {
    this.showIconMessage("close-big", message, timeoutSeconds);
  }

  public show() {
    this._visible.next(true);
  }

  public hide() {
    this._visible.next(false);
    this.setMessage(null);
    this.setIcon(null);
    // a prompt belongs to the activity that raised it and must not outlive it
    this.clearPrompt();
  }

  public loading(loading: boolean, message?: string, backdrop?: boolean) {
    this.setMessage(message);
    this.setBackdrop(backdrop);

    if (loading) {
      this.show();
    } else {
      this.hide();
      this.setBackdrop(false);
    }
  }

  /**
   * hand the wait over to the user. the spinner goes, and its backdrop with it - the card the
   * prompt is rendered as brings a modal backdrop of its own.
   */
  public prompt(prompt: IActivityPrompt) {
    this.hide();
    this.setBackdrop(false);
    this._currentPrompt = prompt;
    this._prompt.next(prompt);
  }

  public clearPrompt() {
    this._currentPrompt = null;
    this._prompt.next(null);
  }

  /**
   * the user asked to try again. everything here is synchronous on purpose: awaiting anything
   * before the callback loses the user gesture, and whatever window it opens is blocked again.
   */
  public retryPrompt() {
    const prompt = this._currentPrompt;
    this.clearPrompt();
    this.loading(true, "", true);
    prompt?.retry();
  }

  public setIcon(icon: string, cssClass?: IconCSSClass) {
    this._icon.next({ name: icon, cssClass });
  }

  public showIconMessage(
    icon: string,
    message: string,
    timeoutSeconds?: number,
    cssClass?: IconCSSClass
  ) {
    this.setIcon(icon, cssClass);
    this.setMessage(message);
    this.show();

    if (timeoutSeconds !== undefined) {
      setTimeout(() => {
        this.hide();
      }, timeoutSeconds * 1000);
    }
  }
}
